import { MouseEvent, useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuthStore, useHasPermission } from "@/store/authStore";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmailInput } from "@/components/ui/email-input";
import { PhoneInput } from "@/components/ui/phone-input";
import { PlusCircle, PowerOff, Power, Info, Upload } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  useTeamsStore,
  User,
  CreateUserData,
  UpdateUserData,
  UserRole,
  UserStatus,
} from "@/store/teamsStore";
import { useRoles } from "@/store/rolesStore";
import {
  CSV_IMPORT_TYPE,
  DashboardInviteStatus,
  DefaultRoleSlug,
  Permission,
  Role,
  UserStatusReportSelectionSchema,
} from "@forgepoint/shared";
import { StatusReportSelectItems } from "@/components/settings/statusReports/StatusReportSelectItems";
import { selectionShortLabel } from "@/components/settings/statusReports/statusReportSelectionOptions";
import { useStatusReportTemplatesStore } from "@/store/statusReportTemplatesStore";
import { toast } from "sonner";
import { captureException } from "@/lib/analytics";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import CsvImportSheet from "@/modules/csvImports/CsvImportSheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export function TeamsController() {
  const {
    users,
    loading,
    error,
    fetchUsers,
    createUser,
    updateUser,
    updateUserStatus,
  } = useTeamsStore();

  const currentUser = useAuthStore((state) => state.user);
  const canEditUsers = useHasPermission(Permission.USERS_EDIT);
  const roles = useRoles();

  /**
   * Resolves the default role id for a new user: the global "user" role if
   * present, otherwise the first available role. Matches on the global default
   * (`organizationId === null`) so a custom org role reusing the "user" slug
   * cannot be picked by mistake.
   * @returns The default role id, or an empty string if no roles are loaded
   */
  const getDefaultRoleId = useCallback((): string => {
    const userRole = roles.find(
      (role) =>
        role.roleSlug === DefaultRoleSlug.USER && role.organizationId === null,
    );
    return userRole?.id ?? roles[0]?.id ?? "";
  }, [roles]);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isImportSheetOpen, setIsImportSheetOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [userToToggle, setUserToToggle] = useState<User | null>(null);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);

  const enabledTemplateKeys = useStatusReportTemplatesStore(
    (state) => state.enabledTemplateKeys,
  );
  const fetchEnabledTemplateKeys = useStatusReportTemplatesStore(
    (state) => state.fetchEnabledTemplateKeys,
  );

  useEffect(() => {
    fetchEnabledTemplateKeys();
  }, [fetchEnabledTemplateKeys]);

  // Form state
  const [formData, setFormData] = useState<CreateUserData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    role: "USER",
    roleId: "",
    dashboardInviteStatus: DashboardInviteStatus.NOT_INVITED,
  });

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Backfill the default role id once roles load while creating a new user.
  useEffect(() => {
    if (isCreating && !formData.roleId && roles.length > 0) {
      setFormData((prev) => ({ ...prev, roleId: getDefaultRoleId() }));
    }
  }, [isCreating, formData.roleId, roles, getDefaultRoleId]);

  const handleRowClick = (user: User) => {
    setSelectedUser(user);
    setFormData({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email,
      phone: user.phone || "",
      role: user.role,
      roleId: user.roleId ?? "",
      dashboardInviteStatus: user.dashboardInviteStatus,
      statusReportTemplate: user.statusReportTemplate,
    });
    setIsCreating(false);
    setIsEmailValid(true); // Existing user emails are assumed valid
    setIsPhoneValid(!!user.phone); // Check if phone exists
    setIsDrawerOpen(true);
  };

  const handleCreateClick = () => {
    setSelectedUser(null);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      role: "USER",
      roleId: getDefaultRoleId(),
      dashboardInviteStatus: DashboardInviteStatus.NOT_INVITED,
    });
    setIsCreating(true);
    setIsEmailValid(false);
    setIsPhoneValid(false);
    setIsDrawerOpen(true);
  };

  const handleSave = async () => {
    try {
      if (isCreating) {
        if (!formData.firstName || !formData.lastName || !formData.email) {
          toast.error("Name and email are required");
          return;
        }

        if (!isEmailValid) {
          toast.error("Please enter a valid email address");
          return;
        }

        if (formData.phone && !isPhoneValid) {
          toast.error("Please enter a valid phone number");
          return;
        }

        if (!formData.roleId) {
          toast.error("Please select a role");
          return;
        }

        const result = await createUser(formData);
        if (result) {
          toast.success("User created successfully");
          setIsDrawerOpen(false);
        }
      } else if (selectedUser) {
        const isSelf = selectedUser.id === currentUser?.id;
        const updates: UpdateUserData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          // Don't allow users to change their own role.
          role: isSelf ? selectedUser.role : formData.role,
          roleId: isSelf ? (selectedUser.roleId ?? undefined) : formData.roleId,
          dashboardInviteStatus: formData.dashboardInviteStatus,
          statusReportTemplate: formData.statusReportTemplate,
        };

        const result = await updateUser(selectedUser.id, updates);
        if (result) {
          toast.success("User updated successfully");
          setIsDrawerOpen(false);
        }
      }
    } catch (error) {
      toast.error("An error occurred while saving");
      captureException(error as Error);
    }
  };

  const handleStatusToggle = (user: User, event: MouseEvent) => {
    event.stopPropagation();

    // Prevent deactivating self
    if (currentUser?.id === user.id) {
      return;
    }

    setUserToToggle(user);
    setStatusDialogOpen(true);
  };

  const handleStatusConfirm = async () => {
    if (userToToggle) {
      try {
        const newStatus =
          userToToggle.status === "ACTIVE" ? "DISABLED" : "ACTIVE";
        const result = await updateUserStatus(userToToggle.id, newStatus);
        if (result) {
          toast.success(`User ${newStatus.toLowerCase()} successfully`);
        }
      } catch (error) {
        toast.error("Failed to update user status");
        captureException(error as Error);
      }
    }
    setStatusDialogOpen(false);
    setUserToToggle(null);
  };

  /**
   * Resolves a user's role by id from the loaded roles.
   * @param roleId - The role id to resolve
   * @returns The matching role, or undefined if not loaded/found
   */
  const getRole = (roleId: string | null): Role | undefined => {
    if (!roleId) {
      return undefined;
    }
    return roles.find((role) => role.id === roleId);
  };

  /**
   * Returns the badge variant for a role: "default" for admins, else
   * "secondary".
   * @param role - The role to derive the badge variant from
   * @returns The badge variant
   */
  const getRoleBadgeVariant = (role: Role | undefined) => {
    return role?.roleSlug === DefaultRoleSlug.ADMIN ? "default" : "secondary";
  };

  /**
   * Selects a role by id and keeps the legacy role enum in sync so the
   * dashboard-invite controls stay driven by whether the role is an admin.
   * @param roleId - The selected role id
   */
  const handleRoleSelect = (roleId: string) => {
    const role = getRole(roleId);
    const legacyRole: UserRole =
      role?.roleSlug === DefaultRoleSlug.ADMIN ? "ADMIN" : "USER";
    setFormData((prev) => ({ ...prev, roleId, role: legacyRole }));
  };

  const getStatusBadgeVariant = (status: UserStatus) => {
    return status === "ACTIVE" ? "default" : "destructive";
  };

  /**
   * Selects a status-report choice from the sheet's picker, ignoring values
   * that don't parse as a valid selection.
   * @param value - The raw select value
   */
  const handleStatusReportSelect = (value: string) => {
    const parsed = UserStatusReportSelectionSchema.safeParse(value);
    if (parsed.success) {
      setFormData((prev) => ({ ...prev, statusReportTemplate: parsed.data }));
    }
  };

  if (error) {
    return <div className="p-4 text-red-500">Error: {error}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center px-3 py-2">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
            Users
          </h2>
          <p className="text-sm text-muted-foreground">
            Manage your users and their roles. Add new users or update existing
            ones.
          </p>
        </div>

        {canEditUsers && (
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setIsImportSheetOpen(true)}
              size="sm"
              className="w-full sm:w-auto"
            >
              <Upload className="mr-2 h-4 w-4" />
              Import
            </Button>
            <Button
              onClick={handleCreateClick}
              size="sm"
              className="w-full sm:w-auto"
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              Add User
            </Button>
          </div>
        )}
      </div>

      <Card>
        {users.length === 0 ? (
          <CardContent className="text-center py-8">
            <p className="text-sm text-muted-foreground">
              {loading ? "Loading..." : "No users found"}
            </p>
          </CardContent>
        ) : (
          <>
            {/* Desktop table view */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status Report</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow
                      key={user.id}
                      className={
                        canEditUsers
                          ? "cursor-pointer hover:bg-muted"
                          : undefined
                      }
                      onClick={
                        canEditUsers ? () => handleRowClick(user) : undefined
                      }
                    >
                      <TableCell>
                        {user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user.email}
                        {currentUser?.id === user.id && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            (You)
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.phone || "-"}</TableCell>
                      <TableCell>
                        <Badge
                          variant={getRoleBadgeVariant(getRole(user.roleId))}
                        >
                          {getRole(user.roleId)?.name ?? user.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {selectionShortLabel(user.statusReportTemplate)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(user.status)}>
                          {user.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleStatusToggle(user, e)}
                                disabled={
                                  loading ||
                                  currentUser?.id === user.id ||
                                  !canEditUsers
                                }
                              >
                                {user.status === "ACTIVE" ? (
                                  <PowerOff className="h-4 w-4" />
                                ) : (
                                  <Power className="h-4 w-4" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                {currentUser?.id === user.id
                                  ? "You cannot deactivate yourself"
                                  : user.status === "ACTIVE"
                                    ? "Disable user"
                                    : "Reactivate user"}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile card view */}
            <div className="md:hidden divide-y">
              {users.map((user) => (
                <div
                  key={user.id}
                  className={`p-4 ${canEditUsers ? "cursor-pointer hover:bg-muted" : ""}`}
                  onClick={
                    canEditUsers ? () => handleRowClick(user) : undefined
                  }
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        {user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user.email}
                        {currentUser?.id === user.id && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            (You)
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground truncate">
                        {user.email}
                      </div>
                      {user.phone && (
                        <div className="text-sm text-muted-foreground">
                          {user.phone}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleStatusToggle(user, e)}
                      disabled={
                        loading || currentUser?.id === user.id || !canEditUsers
                      }
                    >
                      {user.status === "ACTIVE" ? (
                        <PowerOff className="h-4 w-4" />
                      ) : (
                        <Power className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Badge variant={getRoleBadgeVariant(getRole(user.roleId))}>
                      {getRole(user.roleId)?.name ?? user.role}
                    </Badge>
                    <Badge variant="outline">
                      {selectionShortLabel(user.statusReportTemplate)}
                    </Badge>
                    <Badge variant={getStatusBadgeVariant(user.status)}>
                      {user.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      <AlertDialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <AlertDialogContent className="max-w-[calc(100%-2rem)] sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {userToToggle?.status === "ACTIVE"
                ? "Disable User"
                : "Reactivate User"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to{" "}
              {userToToggle?.status === "ACTIVE" ? "disable" : "reactivate"}{" "}
              {userToToggle?.firstName} {userToToggle?.lastName} (
              {userToToggle?.email})?{" "}
              {userToToggle?.status === "ACTIVE"
                ? "This will prevent them from accessing the system. You can re-enable them later."
                : "This will restore their access to the system."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <AlertDialogCancel className="mt-0">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleStatusConfirm}>
              {userToToggle?.status === "ACTIVE"
                ? "Disable User"
                : "Reactivate User"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <SheetContent className="w-full sm:max-w-[600px] md:max-w-[700px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{isCreating ? "Add New User" : "Edit User"}</SheetTitle>
            <SheetDescription>
              {isCreating
                ? "Fill in the details to add a new user"
                : "Make changes to the selected user"}
            </SheetDescription>
          </SheetHeader>

          <div className="py-6">
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                    placeholder="Enter first name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              <EmailInput
                id="email"
                label="Email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                onValidationChange={setIsEmailValid}
                placeholder="Enter email address"
                disabled={!isCreating}
              />

              <PhoneInput
                id="phone"
                label="Phone"
                value={formData.phone}
                onChange={(value: string | undefined) =>
                  setFormData({ ...formData, phone: value || "" })
                }
                enforceValidation={true}
                onValidationChange={setIsPhoneValid}
                placeholder="Enter phone number"
                defaultCountry="US"
              />

              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select
                  value={formData.roleId}
                  onValueChange={handleRoleSelect}
                  disabled={selectedUser?.id === currentUser?.id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!isCreating && (
                <div className="space-y-2">
                  <Label htmlFor="statusReport">Status Report</Label>
                  <Select
                    value={formData.statusReportTemplate}
                    onValueChange={handleStatusReportSelect}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select report" />
                    </SelectTrigger>
                    <SelectContent>
                      <StatusReportSelectItems
                        enabledTemplateKeys={enabledTemplateKeys}
                        currentSelection={formData.statusReportTemplate}
                      />
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    The daily status report this user receives from their agent.
                  </p>
                </div>
              )}

              {formData.role === "USER" && (
                <div className="flex items-center justify-between space-x-2 pt-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="dashboardInviteStatus">
                      Invite to Dashboard
                    </Label>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Info className="h-3 w-3" />
                      Gives dashboard access, emailing an invitation if they
                      have no Forgepoint account yet
                    </p>
                  </div>
                  <Switch
                    id="dashboardInviteStatus"
                    checked={
                      formData.dashboardInviteStatus ===
                      DashboardInviteStatus.INVITED
                    }
                    onCheckedChange={(checked) =>
                      setFormData({
                        ...formData,
                        dashboardInviteStatus: checked
                          ? DashboardInviteStatus.INVITED
                          : DashboardInviteStatus.NOT_INVITED,
                      })
                    }
                  />
                </div>
              )}

              {selectedUser?.id === currentUser?.id && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> You cannot change your own role.
                  </p>
                </div>
              )}

              {formData.role === "ADMIN" &&
                selectedUser?.id !== currentUser?.id &&
                selectedUser?.role !== "ADMIN" && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> Setting this user as an Admin will
                      send them an invitation to access the dashboard.
                    </p>
                  </div>
                )}
            </div>
          </div>

          <SheetFooter className="flex-col sm:flex-row gap-2">
            <Button
              onClick={handleSave}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              {isCreating ? "Add User" : "Save Changes"}
            </Button>
            <SheetClose asChild>
              <Button variant="outline" className="w-full sm:w-auto">
                Cancel
              </Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <CsvImportSheet
        importType={CSV_IMPORT_TYPE.USERS}
        open={isImportSheetOpen}
        onOpenChange={setIsImportSheetOpen}
        onImportComplete={fetchUsers}
      />
    </div>
  );
}
